import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useEventLoader } from "../../../../hooks/Events/useEventLoader";
import { getPaymentByTransactionId } from "../../../../Services/Payment";
import { IPayment } from "../../../../interfaces/models/IPayment";
import { paymentStatusEnum } from "../../../../Enums/RegistrationEnum";

export default function RegistrationSuccess() {
  const { id: eventId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const event = useEventLoader(eventId);
  const [payment, setPayment] = useState<IPayment | null>(null);

  const transactionId = searchParams.get("transactionId");

  useEffect(() => {
    const loadPayment = async () => {
      if (transactionId && eventId && event?.id) {
        try {
          const paymentData = await getPaymentByTransactionId(transactionId);
          if (paymentData && paymentData.eventId === event.id) {
            setPayment(paymentData);
          } else {
            console.warn("Payment found but does not belong to current event", {
              paymentEventId: paymentData?.eventId,
              currentEventId: event.id,
            });
            setPayment(null);
          }
        } catch (error) {
          console.error("Error loading payment:", error);
          setPayment(null);
        }
      }
    };

    loadPayment();
  }, [transactionId, eventId, event?.id]);

  const formatDate = (timestamp?: number) => {
    if (!timestamp) {
      return "N/A";
    }
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8">
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 max-w-2xl w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-green-800 mb-2">
            Payment Successful!
          </h1>
          <p className="text-green-700">
            Your registration has been confirmed.
          </p>
        </div>

        {event && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {event.title}
            </h2>
            {event.startDate && (
              <p className="text-gray-600">
                {new Date(event.startDate).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
          </div>
        )}

        {payment && (
          <div className="bg-white rounded-lg p-6 mb-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Payment Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-semibold text-gray-800">
                  {formatCurrency(
                    payment.lastTransactionAmount !== undefined
                      ? payment.lastTransactionAmount
                      : payment.amount,
                    payment.currency
                  )}
                </span>
              </div>
              {payment.transactionId && payment.transactionId.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Transaction ID:</span>
                  <span className="font-mono text-sm text-gray-800">
                    {payment.transactionId[0]}
                  </span>
                </div>
              )}
              {payment.paymentDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Date:</span>
                  <span className="text-gray-800">
                    {formatDate(payment.paymentDate)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-semibold">
                  {payment.status === paymentStatusEnum.completed
                    ? "Completed"
                    : payment.status}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-800 text-sm">
            A confirmation email has been sent to your registered email address.
            Please check your inbox for payment and registration details.
          </p>
        </div>

        <div className="flex gap-4 justify-center">
          <button
            onClick={() => navigate(`/events/${eventId}`)}
            className="px-6 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
          >
            Back to Event
          </button>
          {eventId && (
            <button
              onClick={() => navigate(`/events/${eventId}/registration`)}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            >
              View Registration
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
